import React, {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import moment from "moment";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import SwitchView from "@/components/SwitchView";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import CardsContainer from "@/components/CardsContainer";
import CardsList from "@/components/CardsList";
import CardsTable from "@/components/CardsTable";
import AnthropometricDataPage from "@/pages/AnthropometricData";
import ImportAthleticTestsModal from "@/pages/Manager/AthleticTests/ImportAthleticTestsModal";

import {AthleticTestAPI} from "@/api/athletic_test";
import {PlayerAPI} from "@/api/player";

import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import UploadFileIcon from "@mui/icons-material/UploadFile";

import {
	ERROR_OPERATION,
	SUCCESSFUL_OPERATION,
	dateFormat,
} from "@/utils/constants";
import {fetchData, getCurrentCategory} from "@/utils/functions";
import {useCategories} from "@/utils/hooks";

import "./index.scss";

const defaultFiltersData = {
	// fromDate: moment().startOf("isoWeek").format("YYYY-MM-DD"),
	// toDate: moment().endOf("isoWeek").format("YYYY-MM-DD"),
};

function AthleticTestsPage(props) {
	const {handleNotification} = props;

	const navigate = useNavigate();

	const userRole = useSelector((state) => state?.user?.roleName);

	const [currentSwitchView, setCurrentSwitchView] = useState("tests");

	const [loading, setLoading] = useState(false);
	const [tests, setTests] = useState([]);
	const [filtersData, setFiltersData] = useState(
		JSON.parse(sessionStorage.getItem("athletic_tests_filters")) ||
			defaultFiltersData,
	);

	const [currentDate, setCurrentDate] = useState();
	const [playersList, setPlayersList] = useState([]);
	const [openImport, setOpenImport] = useState(false);
	const [goalkeepers, setGoalkeepers] = useState([]);

	const seasonId = useSelector((state) => state?.season?.id);

	// Portieri (prospect:false, goalkeeper:true): usati per il matching
	// nell'import dei test OptoJump.
	useEffect(() => {
		const getGoalkeepers = async () => {
			try {
				const response = await PlayerAPI.list({
					prospect: false,
					goalkeeper: true,
					seasonId,
				});
				setGoalkeepers(response || []);
			} catch (error) {
				setGoalkeepers([]);
			}
		};
		if (seasonId) {
			getGoalkeepers();
		}
	}, [seasonId]);

	useEffect(() => {
		if (filtersData) {
			sessionStorage.setItem(
				"athletic_tests_filters",
				JSON.stringify(filtersData),
			);
		}
	}, [filtersData]);

	const {categories} = useCategories({
		setLoading: false,
		handleNotification: undefined,
		onlyPro: true,
		formatted: true,
	});

	const filters = [
		// {
		// 	key: "categoryId",
		// 	type: "select",
		// 	options: categories,
		// 	sorted: false,
		// 	label: "Categoria",
		// 	customClassName: "col-12 col-sm-4",
		// 	clearable: true,
		// 	value: filtersData?.categoryId,
		// 	setValue: (value) => {
		// 		setFiltersData({...filtersData, categoryId: value});
		// 	},
		// },
	];

	const getTests = () => {
		fetchData(
			AthleticTestAPI.list,
			{
				categoryId: filtersData?.categoryId?.value,
			},
			() => setLoading(true),
			setTests,
			() => handleNotification(ERROR_OPERATION, "error"),
			() => setLoading(false),
		);
	};

	useEffect(() => {
		if (categories?.length) {
			getTests();
		}
	}, [filtersData, categories]);

	const saveGrowths = async () => {
		setLoading(true);
		try {
			await PlayerAPI.createGrowths(
				playersList.map((p) => {
					return {
						date: moment(currentDate).format("YYYY-MM-DD"),
						playerId: p.player.value,
						height: p.height || 0,
						sittingHeight: p.sittingHeight || 0,
						weight: p.weight || 0,
					};
				}),
			);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			setCurrentDate();
			setPlayersList([]);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const listConfiguration = {
		data: tests.map((t) => {
			t.currentCategory = getCurrentCategory(categories, t);
			return t;
		}),
		onClick: (test) => navigate("/athletic-tests/" + test.id),
	};

	const columns = [
		{
			label: "Data",
			key: "testDate",
			customRender: (item) => moment(item.testDate).format(dateFormat),
			small: true,
		},
		{
			label: "Giocatore",
			key: "playerId",
			customRender: (item) =>
				item?.player
					? item.player.firstName + " " + item.player.lastName
					: "---",
			align: "start",
		},
		// {
		// 	label: "Categoria",
		// 	key: "playerCategoryId",
		// 	customRender: (item) => (item?.player ? item.player.category : "---"),
		// 	small: true,
		// 	align: "start",
		// },
		{
			label: "Tipologia",
			key: "testType",
			align: "start",
			small: true,
		},
	];

	const mainActions = [];

	if (currentSwitchView === "tests") {
		if (userRole !== "Player") {
			mainActions.push({
				icon: <UploadFileIcon />,
				title: "Importa test",
				onClick: () => setOpenImport(true),
				color: "info",
			});
			mainActions.push({
				icon: <AddIcon />,
				title: "Aggiungi test",
				onClick: () => navigate("/athletic-tests/new"),
			});
		}
	}

	if (currentSwitchView === "anthropometric_data") {
		mainActions.push({
			icon: <SaveIcon />,
			title: "Salva",
			onClick: saveGrowths,
			disabled: !currentDate || !playersList?.length,
		});
	}

	return (
		<Page>
			<Loading visible={loading} />
			<SwitchView
				currentSwitchView={currentSwitchView}
				setCurrentSwitchView={setCurrentSwitchView}
				pageOptions={[
					{value: "tests", label: "Test"},
					{value: "anthropometric_data", label: "Dati antropometrici"},
				]}
			/>
			<Breadcrumbs actions={<MainActions actions={mainActions} />} />
			{currentSwitchView === "tests" ? (
				<CardsContainer
					tableComponent={
						<CardsTable
							fullWidth
							// filtersData={filters}
							configurationColumns={columns}
							data={tests}
							handleClick={(item) => {
								navigate("/athletic-tests/" + item.id);
							}}
							defaultSortedField="testDate"
							defaultSortedOrder="descend"
						/>
					}
					listComponent={
						<CardsList
							type="test"
							breadcrumbs
							// filtersData={filters}
							listConfiguration={listConfiguration}
							defaultSortedField="testDate"
							defaultSortedOrder="descend"
						/>
					}
				/>
			) : null}
			{currentSwitchView === "anthropometric_data" ? (
				<AnthropometricDataPage
					currentDate={currentDate}
					setCurrentDate={setCurrentDate}
					playersList={playersList}
					setPlayersList={setPlayersList}
				/>
			) : null}
			{openImport ? (
				<ImportAthleticTestsModal
					open={openImport}
					onClose={() => setOpenImport(false)}
					players={goalkeepers}
					handleNotification={handleNotification}
					onImported={getTests}
				/>
			) : null}
		</Page>
	);
}

export default AthleticTestsPage;
